import React, { useState } from 'react';
import { useNavigate, Navigate, useLocation } from 'react-router-dom';
import apiClient from '../../api/client';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';
import { CRM_VERSION } from '../../version';

const AdminLogin = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { adminLogin, isAdminAuthenticated } = useAuth();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const from = location.state?.from?.pathname || '/admin/dashboard';

  if (isAdminAuthenticated) {
    return <Navigate to={from} replace />;
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!username.trim() || !password) {
      setError('아이디와 비밀번호를 입력해주세요');
      return;
    }
    setLoading(true);
    try {
      const { data } = await apiClient.post('/admin/auth/login', {
        username: username.trim(),
        password,
      });
      adminLogin(data.token, data.admin);
      toast.success('로그인되었습니다');
      navigate(from, { replace: true });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = typeof detail === 'string' ? detail : '로그인에 실패했습니다';
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center bg-gray-50 px-4"
      data-testid="admin-login-page"
    >
      <div className="w-full max-w-sm">
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-8">
          <div className="mb-8 text-center">
            <div className="text-2xl font-bold text-gray-900 tracking-tight">Insight Fit</div>
            <div className="text-sm text-gray-500 mt-1">CRM 관리자 로그인</div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4" data-testid="admin-login-form">
            <div>
              <label htmlFor="admin-username" className="block text-sm font-medium text-gray-700 mb-1.5">
                아이디
              </label>
              <input
                id="admin-username"
                type="text"
                autoComplete="username"
                autoFocus
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                data-testid="admin-login-username"
                disabled={loading}
              />
            </div>
            <div>
              <label htmlFor="admin-password" className="block text-sm font-medium text-gray-700 mb-1.5">
                비밀번호
              </label>
              <input
                id="admin-password"
                type="password"
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                data-testid="admin-login-password"
                disabled={loading}
              />
            </div>

            {error && (
              <div
                className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-md px-3 py-2"
                data-testid="admin-login-error"
              >
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="w-full py-2.5 bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold rounded-lg transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
              data-testid="admin-login-submit"
            >
              {loading ? '로그인 중…' : '로그인'}
            </button>
          </form>
        </div>

        <div
          className="mt-6 text-center text-xs text-gray-400"
          data-testid="admin-login-version"
        >
          CRM v{CRM_VERSION}
        </div>
      </div>
    </div>
  );
};

export default AdminLogin;
